import merge from "lodash/merge";
import { AxnuiErrorCodeTypes, LocaleTypes } from "@iddataweb/idw-common-ui/enums";
import { AXNUI_CONTENT_PROPERTY_TYPE_ID, CUSTOM_LANGUAGES_PROPERTY_TYPE_ID, DCUI_CONTENT_PROPERTY_TYPE_ID, LOCALIZE_STRINGS_PROPERTY_TYPE_ID } from "@constants/verification-service-details-constants";
import { AXNUIContent, DCUIContent, MultiLanguageWrapper } from "@iddataweb/idw-common-ui/types";
import { FormattedRpServiceConfigurationProperty } from "@idw-types/data/rp-service-configuration";
import { FormattedAppliedProfile } from "@idw-types/data/applied-profile/applied-profile";
import { getProfilePropertyValuesForPropertyTypeId } from "./applied-profile-util";
import { FormattedIdwProfile, FormattedIdwProfileProperty } from "@idw-types/data/idw-profile";
import { setValueAndAddedToProfileForProfileProperty } from "./login-hint/login-hint-utils";
import { DEFAULT_AXNUI_CONTENT, DEFAULT_DCUI_CONTENT } from "@iddataweb/idw-common-ui/constants";

export const checkIfConfigurationContentIsMultiLanguage = <T>(
    content: T | MultiLanguageWrapper<T>
): boolean => {
    const contentKeys = Object.keys(content);
    if (contentKeys.length > 0) {
        const LocaleTypeValues = Object.values(LocaleTypes);
        return contentKeys.every(key => LocaleTypeValues.includes(key as any));
    }

    return false;
};

export const performMergeForContent = <T>(content: T, jsonString: string) => {
    try {
        const jsonObject = JSON.parse(jsonString) as T;
        if (jsonObject) {
            const mergedObject = merge({}, content, jsonObject);
            return JSON.stringify(mergedObject);
        } else {
            return JSON.stringify(content);
        }
    } catch (error) {
        console.log("Error parsing content on service fetch:", error);
        return JSON.stringify(content);
    }
};

const clearEmptyObjects = (obj: any) => {
    for (const key in obj) {
        if (!obj[key] || typeof obj[key] !== "object") {
            continue // If null or not an object, skip to the next iteration
        }

        // The property is an object
        clearEmptyObjects(obj[key]); // <-- Make a recursive call on the nested object
        if (Object.keys(obj[key]).length === 0) {
            delete obj[key]; // The object had no properties, so delete that property
        } else {
            let hasDefinedValue = false;
            Object.keys(obj[key]).forEach((innerKey: any) => {
                if (typeof obj[key][innerKey] !== "undefined") {
                    hasDefinedValue = true;
                }
            });
            if (!hasDefinedValue) {
                delete obj[key];
            }
        }
    }
};

const performUnmergeForContentKey = (
    defaultObject: any,
    newObject: any,
    depth: number,
    parentKey: string
) => {
    const finalContent: { [key: string]: any } = {};
    const allKeysWithDuplicates = [
        ...Object.keys(defaultObject || {}),
        ...Object.keys(newObject || {})
    ];
    const allKeys = Array.from(new Set(allKeysWithDuplicates));
    allKeys.forEach(key => {
        const defaultValue = typeof defaultObject?.[key] === "undefined" ? null : defaultObject?.[key];
        const newValue = typeof newObject?.[key] === "undefined" ? null : newObject?.[key];

        if (
            (typeof defaultValue === "object" && defaultValue !== null) ||
            (typeof newValue === "object" && newValue !== null)
        ) {
            finalContent[key] = performUnmergeForContentKey(
                defaultValue,
                newValue,
                depth + 1,
                `${parentKey}.${key}`
            );
        } else if (typeof defaultValue === "boolean" && defaultValue !== newValue && newValue !== null) {
            finalContent[key] = newValue;
        } else {
            finalContent[key] = (defaultValue === newValue || newValue === null) ? undefined : newValue;
        }
    });
    return finalContent;
};

const performUnmergeForContent = <T>(defaultContent: T, newContent: T) => {
    const unmergedContent: Partial<T> = {};
    const allKeysWithDuplicates = [
        ...Object.keys(defaultContent || {}),
        ...Object.keys(newContent || {})
    ];
    const allKeys = Array.from(new Set(allKeysWithDuplicates)) as Array<keyof T>;

    allKeys.forEach(key => {
        const defaultContentValue = defaultContent[key];
        const newContentValue = newContent[key];

        if (
            (typeof defaultContentValue === "object" && defaultContentValue !== null) ||
            (typeof newContentValue === "object" && newContentValue !== null)
        ) {
            unmergedContent[key] = performUnmergeForContentKey(
                defaultContentValue,
                newContentValue,
                1,
                key as any
            ) as any;
        } else if (typeof defaultContentValue === "boolean" && defaultContentValue !== newContentValue && newContentValue !== null) {
            unmergedContent[key] = newContentValue as any;
        } else {
            unmergedContent[key] = (defaultContentValue === newContentValue || newContentValue === null) ? undefined : newContentValue as any;
        }
    });

    clearEmptyObjects(unmergedContent);
    return unmergedContent;
};

export const performUnmergeForContentOuter = <T>(defaultContent: T, jsonString: string) => {
    try {
        const newContent = JSON.parse(jsonString) as T;
        if (newContent) {
            const finalContent = performUnmergeForContent(defaultContent, newContent);
            return JSON.stringify(finalContent);
        }
    } catch (error) {
        console.log("Error parsing content before service update:", error)
    }
    return jsonString;
};

export const performUnmergeForForMultiLanguageContentOuter = (defaultContent: MultiLanguageWrapper<AXNUIContent | DCUIContent>, jsonString: string, availableLanguages: Array<LocaleTypes>) => {
    try {
        const newContent = JSON.parse(jsonString) as MultiLanguageWrapper<AXNUIContent | DCUIContent>;
        if (newContent) {
            const finalContent = availableLanguages.reduce((result, language) => {
                const languageContent = newContent[language];
                result[language] = performUnmergeForContent<AXNUIContent | DCUIContent>(defaultContent[language], languageContent);

                return result;
            }, {} as any);

            return JSON.stringify(finalContent);
        }
    } catch (error) {
        console.log("Error parsing content before service update:", error)
    }
    return jsonString;
};

export const getUpdatedPropertiesForContentPropertyUpdate = <T>(
    propertyTypeId: string,
    properties: Array<FormattedRpServiceConfigurationProperty>,
    newContent: T,
    multiLanguageContent: MultiLanguageWrapper<T>,
    availableLanguages: Array<LocaleTypes>,
    selectedLanguage: LocaleTypes,
    isMultiLanguageEnabled: boolean,
    defaultContent: MultiLanguageWrapper<T>
) => {
    const newProperties = properties.map(property => {
        if (property.propertyTypeId === propertyTypeId) {
            const newValue = generateValueForContentProperty(isMultiLanguageEnabled, multiLanguageContent, availableLanguages, selectedLanguage, newContent, defaultContent);

            return {
                ...property,
                value: newValue,
                addedToService: true
            };

        } else {
            return property;
        }
    });

    return newProperties;
};

export const getAxnuiContent = (properties: Array<FormattedRpServiceConfigurationProperty>) => {
    const axnuiContent = properties.find(property => property.propertyTypeId === AXNUI_CONTENT_PROPERTY_TYPE_ID);
    if (axnuiContent?.value) {
        try {
            return JSON.parse(axnuiContent.value);
        } catch (error) {
            console.error(error);
        }
    }

    return {};
};

export const ERROR_CODE_NONE_LABEL = " None";

export const getThemingErrorCodes = () => {
    return Object.keys(AxnuiErrorCodeTypes);
};

export const extractContentValuesFromProfileProperties = <T>(
    appliedProfiles: Array<FormattedAppliedProfile>,
    propertyTypeId: string,
    defaultContent: MultiLanguageWrapper<T>,
    selectedLanguage: LocaleTypes
) => {
    const contentProfileProperties = getProfilePropertyValuesForPropertyTypeId(
        appliedProfiles,
        propertyTypeId
    ) as any as Array<T | MultiLanguageWrapper<T>>;

    const availableLanguages: Array<LocaleTypes> = [];
    let multiLanguageContent: MultiLanguageWrapper<T> = null;
    let mergedContent: T = defaultContent[selectedLanguage];

    contentProfileProperties.forEach(cpp => {
        // find out if multi language
        const isMultiLanguageFormat = checkIfConfigurationContentIsMultiLanguage(cpp);

        if (isMultiLanguageFormat) {
            // store all of the languages that are present in availableLanguages array
            for (const localeTypeValue of Object.values(LocaleTypes)) {
                const localeType: LocaleTypes = localeTypeValue;
                if ((cpp as MultiLanguageWrapper<T>)[localeType] && !availableLanguages.includes(localeType)) {
                    availableLanguages.push(localeType);
                }
            }

            //updated the merged content object
            mergedContent = merge({}, mergedContent, (cpp as MultiLanguageWrapper<T>)[selectedLanguage]);

            // update multi language content object with new data
            if (multiLanguageContent === null) {
                multiLanguageContent = merge({}, defaultContent, cpp);
            } else {
                multiLanguageContent = merge({}, multiLanguageContent, cpp);
            }
        } else {
            //if its not multilanguage, we know the content is for english, so only
            //merge in the content if the selected language is english
            if (selectedLanguage === LocaleTypes.ENGLISH) {
                mergedContent = merge({}, mergedContent, cpp);
            }

            const partialContent: Partial<MultiLanguageWrapper<T>> = { [LocaleTypes.ENGLISH]: cpp as T };
            // update multi language content object with new data
            if (multiLanguageContent === null) {
                multiLanguageContent = merge({}, defaultContent, partialContent);
            } else {
                multiLanguageContent = merge({}, multiLanguageContent, partialContent);
            }
        }
    });

    return {
        availableLanguages,
        mergedContent,
        multiLanguageContent
    };
};

export const setAXNUIAndDCUIContentPropertyValue = (
    properties: Array<FormattedRpServiceConfigurationProperty>,
    isMultiLanguageEnabled: boolean,
    multiLanguageAXNUIContent: MultiLanguageWrapper<AXNUIContent>,
    multiLanguageDCUIContent: MultiLanguageWrapper<DCUIContent>,
    availableLanguages: Array<LocaleTypes>,
    selectedLanguage: LocaleTypes
) => {
    const newProperties = properties.map(property => {
        if (property.propertyTypeId === AXNUI_CONTENT_PROPERTY_TYPE_ID) {
            const newValue = generateValueForContentProperty(
                isMultiLanguageEnabled,
                multiLanguageAXNUIContent,
                availableLanguages,
                selectedLanguage,
                multiLanguageAXNUIContent[LocaleTypes.ENGLISH],
                DEFAULT_AXNUI_CONTENT
            );

            return {
                ...property,
                value: newValue,
                addedToService: true
            };

        } else if (property.propertyTypeId === DCUI_CONTENT_PROPERTY_TYPE_ID) {
            const newValue = generateValueForContentProperty(
                isMultiLanguageEnabled,
                multiLanguageDCUIContent,
                availableLanguages,
                selectedLanguage,
                multiLanguageDCUIContent[LocaleTypes.ENGLISH],
                DEFAULT_DCUI_CONTENT
            );

            return {
                ...property,
                value: newValue,
                addedToService: true
            };
        } else {
            return property;
        }
    });

    return newProperties;
};

export const setAXNUIAndDCUIContentProfilePropertyValue = (
    workflowProfile: FormattedIdwProfile,
    isMultiLanguageEnabled: boolean,
    multiLanguageAXNUIContent: MultiLanguageWrapper<AXNUIContent>,
    multiLanguageDCUIContent: MultiLanguageWrapper<DCUIContent>,
    availableLanguages: Array<LocaleTypes>,
    selectedLanguage: LocaleTypes,
    shouldEnableLocalizeString: boolean
): Array<FormattedIdwProfileProperty> => {

    return workflowProfile.profileProperties.map(property => {
        if (property.availableProperty?.id === AXNUI_CONTENT_PROPERTY_TYPE_ID) {
            return generateContentProfileProperty(
                property,
                isMultiLanguageEnabled,
                multiLanguageAXNUIContent,
                availableLanguages,
                selectedLanguage,
                multiLanguageAXNUIContent[LocaleTypes.ENGLISH],
                DEFAULT_AXNUI_CONTENT
            );
        }

        if (property.availableProperty?.id === DCUI_CONTENT_PROPERTY_TYPE_ID) {
            return generateContentProfileProperty(
                property,
                isMultiLanguageEnabled,
                multiLanguageDCUIContent,
                availableLanguages,
                selectedLanguage,
                multiLanguageDCUIContent[LocaleTypes.ENGLISH],
                DEFAULT_DCUI_CONTENT
            );
        }

        if (shouldEnableLocalizeString && property.availableProperty?.id === LOCALIZE_STRINGS_PROPERTY_TYPE_ID) {
            return {
                ...property,
                addedToProfile: shouldEnableLocalizeString,
                value: 'true'
            }
        }

        if (property.availableProperty?.id === CUSTOM_LANGUAGES_PROPERTY_TYPE_ID) {
            return {
                ...property,
                addedToProfile: isMultiLanguageEnabled,
                value: isMultiLanguageEnabled ? availableLanguages.join(',') : null
            }
        }

        return property;
    });
};

export const getMergedMultiLanguageContent = <T>(
    multiLanguageContent: MultiLanguageWrapper<T>,
    availableLanguages: Array<LocaleTypes>,
    selectedLanguage: LocaleTypes,
    newContent: T,
    defaultContent: MultiLanguageWrapper<T>
) => {
    const newMultiLanguageContent: { [key in LocaleTypes]?: T } = {};
    availableLanguages.forEach(availableLanguage => {
        if (selectedLanguage === availableLanguage) {
            newMultiLanguageContent[selectedLanguage] = newContent;
        } else {
            if (multiLanguageContent[availableLanguage]) {
                newMultiLanguageContent[availableLanguage] = multiLanguageContent[availableLanguage];
            } else {
                newMultiLanguageContent[availableLanguage] = defaultContent[availableLanguage];
            }
        }
    });
    return newMultiLanguageContent;
};

export const getMergedMultiLanguageOrSingleLanguageContent = <T>(
    isMultiLanguageEnabled: boolean,
    multiLanguageContent: MultiLanguageWrapper<T>,
    availableLanguages: Array<LocaleTypes>,
    selectedLanguage: LocaleTypes,
    newContent: T,
    defaultContent: MultiLanguageWrapper<T>
) => {
    if (isMultiLanguageEnabled && availableLanguages.length > 0) {
        return getMergedMultiLanguageContent(multiLanguageContent, availableLanguages, selectedLanguage, newContent, defaultContent)
    } else {
        return newContent;
    }
};

export const generateValueForContentProperty = <T>(
    isMultiLanguageEnabled: boolean,
    multiLanguageContent: MultiLanguageWrapper<T>,
    availableLanguages: Array<LocaleTypes>,
    selectedLanguage: LocaleTypes,
    newContent: T,
    defaultContent: MultiLanguageWrapper<T>
) => {
    const newValue = getMergedMultiLanguageOrSingleLanguageContent<T>(
        isMultiLanguageEnabled,
        multiLanguageContent,
        availableLanguages,
        selectedLanguage,
        newContent,
        defaultContent
    );

    return JSON.stringify(newValue);
}

export const generateContentProfileProperty = <T>(
    property: FormattedIdwProfileProperty,
    isMultiLanguageEnabled: boolean,
    multiLanguageContent: MultiLanguageWrapper<T>,
    availableLanguages: Array<LocaleTypes>,
    selectedLanguage: LocaleTypes,
    newContent: T,
    defaultContent: MultiLanguageWrapper<T>
) => {

    const newValue = generateValueForContentProperty(isMultiLanguageEnabled, multiLanguageContent, availableLanguages, selectedLanguage, newContent, defaultContent);
    return setValueAndAddedToProfileForProfileProperty(property, newValue, true);
}

export const getParsedContentPropertyValue = <T>(
    isMultiLanguageEnabled: boolean,
    multiLanguageContent: MultiLanguageWrapper<T>,
    property: FormattedIdwProfileProperty,
    selectedLanguage: LocaleTypes
) => {
    if (isMultiLanguageEnabled && multiLanguageContent) {
        return JSON.parse(property.value)[selectedLanguage] as T;
    } else {
        return JSON.parse(property.value) as T;
    }
};